import {
  Button,
  Card,
  CardHeader,
  Input,
  Modal,
  StatusPill,
  formatCivilDate,
  formatCurrency,
  formatMonth,
  useSnackbar,
  type CivilDate,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarCheck, CircleAlert, LockKeyholeOpen, Wallet } from 'lucide-react';
import { useState } from 'react';
import {
  financialClosingApi,
  getApiError,
  type FinancialClosing,
  type FinancialClosingAccount,
  type FinancialClosingAlert,
  type FinancialClosingBlocker,
  type FinancialObligationDirection,
} from '../../lib/api.js';
import { useOperationalPeriod } from '../../app/operational-period-context.js';
import { EmptyCard, ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';

export interface ClosingCapabilities {
  canClose: boolean;
  canReopen: boolean;
}

/// O fechamento é por mês de caixa, e o mês vem da casca. "Todos os períodos" não é um caixa: não
/// existe saldo inicial nem final de um recorte sem começo.
export function CompanyClosing({
  capabilities,
  today,
  onOpenReconciliation,
  onOpenObligations,
}: {
  capabilities: ClosingCapabilities;
  today: CivilDate;
  onOpenReconciliation: () => void;
  onOpenObligations: (direction: FinancialObligationDirection) => void;
}) {
  const period = useOperationalPeriod(today);
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [reopening, setReopening] = useState(false);
  const [failure, setFailure] = useState<string>();
  const [working, setWorking] = useState(false);

  const month = period.month === 'ALL' ? null : `${period.month}-01`;
  const closing = useQuery({
    queryKey: ['company', 'finance', 'closing', month],
    queryFn: () => financialClosingApi.get(month!),
    enabled: month !== null,
    retry: false,
  });

  async function refresh() {
    await queryClient.invalidateQueries({ queryKey: ['company', 'finance'] });
  }

  async function close() {
    setWorking(true);
    setFailure(undefined);
    try {
      await financialClosingApi.close(month!);
      await refresh();
      snackbar.success(`${formatMonth(month!)} fechado.`);
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setWorking(false);
    }
  }

  if (!month) {
    return (
      <EmptyCard
        icon={CalendarCheck}
        title="Escolha um mês para fechar"
        description="O fechamento confere o caixa de um mês. Selecione o mês no topo da página."
      />
    );
  }
  if (closing.error) return <ErrorCard message={getApiError(closing.error)} />;
  if (!closing.data) return <LoadingCard label="Carregando o fechamento" />;

  const data = closing.data;
  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <CardHeader title={formatMonth(month)} icon={<CalendarCheck className="size-4" />} />
              <StatusPill tone={data.status === 'CLOSED' ? 'positive' : 'progress'}>
                {data.status === 'CLOSED' ? 'Mês fechado' : 'Mês aberto'}
              </StatusPill>
            </div>
            <p className="mt-2 text-sm text-content-muted">{summaryOf(data)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.status === 'CLOSED' && capabilities.canReopen && data.canReopen ? (
              <Button variant="outline" onClick={() => setReopening(true)}>
                <LockKeyholeOpen className="size-4" aria-hidden="true" />
                Reabrir mês
              </Button>
            ) : null}
            {data.status === 'OPEN' && capabilities.canClose ? (
              <Button onClick={() => void close()} disabled={!data.canClose || working}>
                {working ? 'Fechando…' : 'Fechar mês'}
              </Button>
            ) : null}
          </div>
        </div>
        {failure ? <RequestAlert message={failure} /> : null}
      </Card>

      <Card>
        <CardHeader title="Caixa do mês" icon={<Wallet className="size-4" aria-hidden="true" />} />
        {data.accounts.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nenhuma conta movimentou dinheiro até o fim deste mês.
          </p>
        ) : (
          <AccountTable accounts={data.accounts} />
        )}
      </Card>

      {data.status === 'OPEN' ? (
        <Card>
          <CardHeader
            title="Movimentos sem tratamento"
            icon={<CircleAlert className="size-4" aria-hidden="true" />}
            action={
              data.blockerCount > 0 ? (
                <Button variant="ghost" onClick={onOpenReconciliation}>
                  Ir para a conciliação
                </Button>
              ) : undefined
            }
          />
          {data.blockerCount === 0 ? (
            <p className="text-sm text-content-muted">
              Todo movimento do mês está conciliado, marcado como sem compromisso ou com a diferença
              resolvida. O caixa pode fechar.
            </p>
          ) : (
            <>
              <p className="mb-4 text-sm text-warning-400">{blockerNote(data)}</p>
              <ul className="space-y-2">
                {data.blockers.map((blocker) => (
                  <BlockerRow key={blocker.movementId} blocker={blocker} />
                ))}
              </ul>
            </>
          )}
        </Card>
      ) : null}

      <Card>
        <CardHeader
          title="Compromissos do mês"
          icon={<CalendarCheck className="size-4" aria-hidden="true" />}
        />
        {data.alerts.length === 0 ? (
          <p className="text-sm text-content-muted">
            Nenhum compromisso com saldo pertence a este mês.
          </p>
        ) : (
          <>
            <ul className="space-y-2">
              {data.alerts.map((alert) => (
                <AlertRow key={`${alert.currency}-${alert.direction}`} alert={alert} />
              ))}
            </ul>
            {/* Compromisso em aberto não impede conferir o banco: ele atravessa meses até ser pago. */}
            <p className="mt-4 text-sm text-content-muted">
              Compromisso em aberto não impede o fechamento: ele atravessa meses até ser pago.
            </p>
            <div className="mt-2 flex flex-wrap gap-4">
              {(['PAYABLE', 'RECEIVABLE'] as const)
                .filter((direction) => data.alerts.some((alert) => alert.direction === direction))
                .map((direction) => (
                  <Button
                    key={direction}
                    variant="ghost"
                    className="px-0"
                    onClick={() => onOpenObligations(direction)}
                  >
                    {direction === 'PAYABLE' ? 'Ver em Despesas' : 'Ver em Receitas'}
                  </Button>
                ))}
            </div>
          </>
        )}
      </Card>

      {reopening ? (
        <ReopenModal
          month={month}
          onClose={() => setReopening(false)}
          onReopened={async () => {
            setReopening(false);
            await refresh();
            snackbar.success(`${formatMonth(month)} reaberto.`);
          }}
        />
      ) : null}
    </div>
  );
}

function summaryOf(closing: FinancialClosing): string {
  if (closing.status === 'CLOSED') {
    const when = formatCivilDate(closing.closedAt.slice(0, 10));
    return `Fotografia da versão ${closing.version}, gravada em ${when}. Movimentos e conciliações deste mês estão travados.`;
  }
  if (closing.reopenedReason) {
    return `Mês reaberto: ${closing.reopenedReason}`;
  }
  if (!closing.canClose && closing.chain.startMonth !== null && !closing.chain.previousClosed) {
    return `Feche ${formatMonth(closing.chain.previousMonth)} antes: o fechamento segue a ordem dos meses.`;
  }
  return 'Confira o caixa do mês antes de fechar. Depois de fechado, movimento e conciliação deste período não mudam mais.';
}

function blockerNote(closing: FinancialClosing): string {
  const shown = closing.blockers.length;
  const total = closing.blockerCount;
  if (total === 1) return 'Um movimento ainda não foi conciliado nem marcado como sem compromisso.';
  const start = `${total} movimentos ainda não foram conciliados nem marcados como sem compromisso.`;
  return shown < total ? `${start} Mostrando os ${shown} mais antigos.` : start;
}

function AccountTable({ accounts }: { accounts: FinancialClosingAccount[] }) {
  return (
    <>
      {/* Tabela larga não cabe em telefone: no estreito cada conta vira um cartão. */}
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-surface-border text-xs text-content-faint">
              <th className="py-2 pr-4 font-semibold">Conta</th>
              <th className="py-2 pr-4 text-right font-semibold">Saldo inicial</th>
              <th className="py-2 pr-4 text-right font-semibold">Entradas</th>
              <th className="py-2 pr-4 text-right font-semibold">Saídas</th>
              <th className="py-2 text-right font-semibold">Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.financeAccountId} className="border-b border-surface-border/60">
                <td className="py-3 pr-4">
                  <p className="font-semibold">{account.accountName}</p>
                  <p className="text-xs text-content-faint">{movementNote(account)}</p>
                </td>
                <td className="py-3 pr-4 text-right tabular-nums">
                  {formatCurrency(account.openingBalanceCents, account.currency)}
                </td>
                <td className="py-3 pr-4 text-right tabular-nums text-success-400">
                  {formatCurrency(account.inflowCents, account.currency)}
                </td>
                <td className="py-3 pr-4 text-right tabular-nums text-danger-400">
                  {formatCurrency(account.outflowCents, account.currency)}
                </td>
                <td className="py-3 text-right font-bold tabular-nums">
                  {formatCurrency(account.closingBalanceCents, account.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="space-y-3 md:hidden">
        {accounts.map((account) => (
          <li
            key={account.financeAccountId}
            className="rounded-xl border border-surface-border p-4"
          >
            <p className="font-semibold">{account.accountName}</p>
            <p className="text-xs text-content-faint">{movementNote(account)}</p>
            <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <Figure
                label="Saldo inicial"
                value={formatCurrency(account.openingBalanceCents, account.currency)}
              />
              <Figure
                label="Saldo final"
                value={formatCurrency(account.closingBalanceCents, account.currency)}
              />
              <Figure
                label="Entradas"
                value={formatCurrency(account.inflowCents, account.currency)}
              />
              <Figure
                label="Saídas"
                value={formatCurrency(account.outflowCents, account.currency)}
              />
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-content-faint">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function movementNote(account: FinancialClosingAccount): string {
  if (account.movementCount === 0) return 'Sem movimento neste mês';
  const parts = [`${account.movementCount} movimentos`];
  if (account.settledCount > 0) parts.push(`${account.settledCount} conciliados`);
  if (account.ignoredCount > 0) parts.push(`${account.ignoredCount} sem compromisso`);
  if (account.resolvedCount > 0) parts.push(`${account.resolvedCount} com diferença resolvida`);
  if (account.pendingCount > 0) parts.push(`${account.pendingCount} pendentes`);
  return parts.join(' · ');
}

function BlockerRow({ blocker }: { blocker: FinancialClosingBlocker }) {
  const residual = blocker.amountCents - blocker.settledCents;
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl border border-surface-border px-4 py-3">
      <div className="min-w-0">
        <p className="truncate font-semibold">{blocker.description}</p>
        <p className="text-xs text-content-faint">
          {formatCivilDate(blocker.occurredOn)} · {blocker.accountName}
        </p>
      </div>
      <p className="text-sm font-bold tabular-nums">
        {blocker.settledCents > 0
          ? `Falta ${formatCurrency(residual, blocker.currency)}`
          : formatCurrency(blocker.amountCents, blocker.currency)}
      </p>
    </li>
  );
}

function AlertRow({ alert }: { alert: FinancialClosingAlert }) {
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-2 rounded-xl border border-surface-border px-4 py-3">
      <p className="font-semibold">
        {alert.direction === 'PAYABLE'
          ? `A pagar · ${alert.count} ${alert.count === 1 ? 'despesa' : 'despesas'}`
          : `A receber · ${alert.count} ${alert.count === 1 ? 'receita' : 'receitas'}`}
      </p>
      <p className="text-sm font-bold tabular-nums">
        {formatCurrency(alert.outstandingCents, alert.currency)}
      </p>
    </li>
  );
}

function ReopenModal({
  month,
  onClose,
  onReopened,
}: {
  month: CivilDate;
  onClose: () => void;
  onReopened: () => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string>();

  async function submit() {
    setSaving(true);
    setFailure(undefined);
    try {
      await financialClosingApi.reopen(month, reason);
      await onReopened();
    } catch (error) {
      setFailure(getApiError(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      // O diálogo nasce aberto e só é desmontado ao fechar: a única mudança que chega aqui é o
      // fechamento, venha ele do X, do Esc ou do clique fora.
      onOpenChange={() => onClose()}
      title={`Reabrir ${formatMonth(month)}`}
      description="A fotografia gravada continua no histórico. Fechar de novo cria uma versão nova, e o motivo fica na auditoria."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={reason.trim().length < 3 || saving}>
            {saving ? 'Reabrindo…' : 'Reabrir'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input
          label="Por que este mês precisa reabrir?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Movimento lançado em duplicidade"
          maxLength={240}
        />
        {failure ? <p className="text-sm font-semibold text-danger-400">{failure}</p> : null}
      </div>
    </Modal>
  );
}
